import React, { FC, useEffect, useRef, useState } from 'react'
import { renderToStaticMarkup } from "react-dom/server";
import DOMPurify from "dompurify";
import Quill from "quill";
import Delta from "quill-delta";
import QuillTableBetter from "quill-table-better";

import { AiOutlineBold, AiOutlineItalic, AiOutlineUnderline, AiOutlineStrikethrough, AiOutlineTable } from "react-icons/ai";
import { BiUndo, BiRedo, BiChevronLeft } from "react-icons/bi";

import { EDITOR_FONT_SIZES, EDITOR_HEADINGS, EDITOR_HISTORY_SETTINGS, EDITOR_TABLE_SETTINGS } from '../../../config/documentEditor';
import { QuillHelper } from '../../helpers/quillHelper';
import { ChatHelper } from '../../helpers/chatHelper';
import { applyDocumentTableClassToHtml, registerReveFormats, reveKeyboardBindings } from '../../helpers/quillFormats';
import { IMessageMetadata, IMessageMetadataPolicy } from '../../../types/chat';
import { resolveCitationHref } from '../../../utils/backendUrl';
import type { AppealLetterFormat } from '../../../services/apiService';

import "quill/dist/quill.snow.css";
import "quill-table-better/dist/quill-table-better.css";
import "./DocumentEditor.css";
import "./LetterStylesheet.css";
import { useCapabilities } from '../../../contexts/permissions/useCapabilities';

Quill.register({ "modules/table-better": QuillTableBetter }, true);

/**
 * quill-table-better: setContents / dangerouslyPasteHTML break table layout on load.
 */
function loadQuillHtmlForTableBetter(quill: Quill, html: string) {
    const trimmed = html?.trim() ?? "";
    if (!trimmed) {
        quill.setContents(new Delta().insert("\n"), "silent");
        quill.setSelection(0, 0, "silent");
        return;
    }
    const stripped = QuillHelper.stripCodeBlocksToPlainParagraphs(trimmed);
    const prepared = QuillHelper.inlineLetterTableSemantics(stripped);
    quill.setContents(new Delta().insert("\n"), "silent");
    // Letter HTML can come from another user; sanitize before it reaches the
    // editor (defense in depth for GHSA-v3m3-f69x-jf25, unpatched in quill 2.0.3).
    quill.clipboard.dangerouslyPasteHTML(0, DOMPurify.sanitize(prepared), "silent");
}

function updateAlignMenuState(quill: Quill): void {
    try {
        const range = quill.getSelection();
        const format = range ? quill.getFormat(range) : {};
        const alignValue = format.align as string | false | undefined;
        const listValue = format.list as string | undefined;

        const stateMap: Record<string, boolean> = {
            'align-left': !alignValue,
            'align-center': alignValue === 'center',
            'align-right': alignValue === 'right',
            'align-justify': alignValue === 'justify',
            'ordered-list': listValue === 'ordered',
            'bullet-list': listValue === 'bullet',
        };

        Object.entries(stateMap).forEach(([action, isActive]) => {
            const item = document.querySelector(`.align-options-item[data-action="${action}"]`);
            if (item) item.classList.toggle('align-options-active', isActive);
        });

        const alignBtn = document.querySelector('button.ql-align-options');
        const hasNonDefault = alignValue === 'center' || alignValue === 'right' || alignValue === 'justify' || !!listValue;
        if (alignBtn) alignBtn.classList.toggle('ql-active', hasNonDefault);
    } catch {
        // no action needed
    }
}

function updateHeaderMenuState(quill: Quill): void {
    try {
        const range = quill.getSelection();
        const format = range ? quill.getFormat(range) : {};
        const headerValue = format.header;

        EDITOR_HEADINGS.forEach((heading) => {
            const item = document.querySelector(`.header-options-item[data-header="${heading}"]`);
            const isActive = heading === 'false' ? !headerValue : String(headerValue) === heading;
            if (item) item.classList.toggle('header-options-active', isActive);
        });
    } catch {
        // no action needed
    }
}

function updateMoreOptionsMenuState(quill: Quill): void {
    try {
        const range = quill.getSelection();
        const format = range ? quill.getFormat(range) : {};

        const stateMap: Record<string, boolean> = {
            'link': !!format.link,
            'subscript': format.script === 'sub',
            'superscript': format.script === 'super',
            'blockquote': format.blockquote === true,
        };

        Object.entries(stateMap).forEach(([action, isActive]) => {
            const item = document.querySelector(`.more-options-item[data-action="${action}"]`);
            if (item) item.classList.toggle('more-options-active', isActive);
        });

        const moreBtn = document.querySelector('button.ql-more-options');
        const hasActive = Object.values(stateMap).some(Boolean);
        if (moreBtn) moreBtn.classList.toggle('ql-active', hasActive);
    } catch {
        // no action needed
    }
}

function updateFormatLabels(quill: Quill): void {
    try {
        const range = quill.getSelection();
        const format = range ? quill.getFormat(range) : {};

        const sizeEl = document.querySelector('button.ql-size .ql-size-text') as HTMLElement | null;
        if (sizeEl) {
            const size = format.size as string | undefined;
            sizeEl.textContent = size ? size.replace('px', '') : '12';
        }

        const headerEl = document.querySelector('button.ql-header .ql-header-text') as HTMLElement | null;
        if (headerEl) {
            const header = format.header;
            headerEl.textContent = header ? `H${header}` : 'Normal';
        }

        updateAlignMenuState(quill);
        updateHeaderMenuState(quill);
        updateMoreOptionsMenuState(quill);
    } catch {
        // no action needed
    }
}

/** Clears undo/redo stacks after programmatic load; keeps toolbar in sync with Quill history. */
function resetQuillHistoryAfterLoad(quill: Quill): void {
    quill.history.clear();
    updateHistoryToolbarButtons(quill);
}

function updateHistoryToolbarButtons(quill: Quill): void {
    try {
        const undoBtn = document.getElementById("ql-undo") as HTMLButtonElement | null;
        const redoBtn = document.getElementById("ql-redo") as HTMLButtonElement | null;
        if (!undoBtn || !redoBtn) return;

        const history = quill.history;
        // Quill records one stack item per undo step; length 1 means one edit can still be undone.
        undoBtn.disabled = history.stack.undo.length === 0;
        redoBtn.disabled = history.stack.redo.length === 0;
    } catch (error) {
        console.error("Error updating history buttons:", error);
    }
}

function convertRefToAnchor(html: string, policies: IMessageMetadataPolicy[]): string {
    if (!html) return html;
    const refTagRegex = /<ref\s+([^>]*?)>(.*?)<\/ref>/gi;
    html = html.replace(refTagRegex, (_match, attributes, innerContent) => {
        const idMatch = attributes.match(/id=["']([^"']+)["']/i);
        const id = idMatch ? idMatch[1] : '';
        let href: string | null = null;
        if (id) {
            const matchingPolicy = policies.find(p => String(p.id) === String(id));
            // A tenant contract's source_url is a root-relative API path (REV-2434).
            href = resolveCitationHref(matchingPolicy?.source_url) ?? null;
        }
        if (!href) return innerContent;
        const otherAttrs = attributes.replace(/id=["'][^"']+["']/gi, '').trim();
        return `<a href="${href}" ${otherAttrs}>${innerContent}</a>`;
    });
    const selfClosingRefRegex = /<ref\s+([^>]*?)\s*\/>/gi;
    html = html.replace(selfClosingRefRegex, (_match, attributes) => {
        const idMatch = attributes.match(/id=["']([^"']+)["']/i);
        const id = idMatch ? idMatch[1] : '';
        let href: string | null = null;
        if (id) {
            const matchingPolicy = policies.find(p => String(p.id) === String(id));
            // A tenant contract's source_url is a root-relative API path (REV-2434).
            href = resolveCitationHref(matchingPolicy?.source_url) ?? null;
        }
        if (!href) return '';
        const otherAttrs = attributes.replace(/id=["'][^"']+["']/gi, '').trim();
        return `<a href="${href}" ${otherAttrs}></a>`;
    });
    return html;
}

interface DocumentEditorProps {
    content: string
    /** Required unless `hideHeader`, since the close button lives in the header */
    onClose?: () => void
    onBack?: () => void
    canGoBack?: boolean
    onChange?: (content: string) => void
    onSave?: (content: string) => void
    onTogglePosition?: () => void
    metadata?: IMessageMetadata | null
    disableEditMode?: boolean
    claimId?: string
    title?: string
    /** Hide the header row (back, undo/redo, title, downloads, close) */
    hideHeader?: boolean
    /** Extra px taken off the editor's height, for space the host uses inside the same scroll area */
    heightOffset?: number
    /** Flush pending edits, then download the server-rendered letter (REV-2047). */
    onDownload?: (format: AppealLetterFormat) => Promise<void>
}

const DocumentEditor: FC<DocumentEditorProps> = ({ content, onChange, onSave, onDownload, onClose, onBack, canGoBack, onTogglePosition, metadata, disableEditMode, claimId, title, hideHeader, heightOffset = 0 }) => {
    const { canDownloadAttachments } = useCapabilities();
    const [downloadingFormat, setDownloadingFormat] = useState<AppealLetterFormat | null>(null);
    const editorRef = useRef<HTMLDivElement | null>(null);
    const quillRef = useRef<Quill | null>(null);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    /** Raw HTML last passed into loadQuillHtmlForTableBetter; avoids preview reload loops after Quill inlines letter table styles. */
    const lastLoadedLetterHtmlRef = useRef<string | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);

    const metadataRef = useRef(metadata);
    metadataRef.current = metadata;
    const onSaveRef = useRef(onSave);
    onSaveRef.current = onSave;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    // Read by calculateEditorHeight, which runs from listeners set up once on mount
    const heightOffsetRef = useRef(heightOffset);
    heightOffsetRef.current = heightOffset;

    /**
     * Ask the parent for a download and keep both buttons quiet until it settles.
     *
     * The letter is rendered by the backend from the saved content, so there is
     * nothing to build here — but the parent has to commit pending edits first,
     * which takes a round trip worth disabling the buttons for.
     */
    const handleDownload = async (format: AppealLetterFormat) => {
        if (!onDownload || downloadingFormat) return;

        setDownloadingFormat(format);
        try {
            await onDownload(format);
        } finally {
            setDownloadingFormat(null);
        }
    };

    // Function to calculate and set editor height based on toolbar
    const calculateEditorHeight = () => {
        try{
            if (!editorRef.current || !containerRef.current) return;
        
            const toolbar = containerRef.current.querySelector('.ql-toolbar.ql-snow') as HTMLElement;
            const quillContainer = editorRef.current.querySelector('.ql-container.ql-snow') as HTMLElement;
            const editor = editorRef.current.querySelector('.ql-editor') as HTMLElement;
            
            if (toolbar && quillContainer && editor) {
                const toolbarHeight = toolbar.offsetHeight;
                const containerHeight = containerRef.current.offsetHeight;
                const editorHeight = containerHeight - toolbarHeight - heightOffsetRef.current;
                
                editor.style.height = `${editorHeight}px`;
                editor.style.overflow = 'auto'
            }
        } catch {
            // No action needed
        }
    };


    useEffect(() => {
        if (!editorRef.current || quillRef.current) return;
        
        // Check if toolbar already set, prevent rerender
        const hasToolbar = editorRef.current.querySelector('.ql-tooltip.ql-hidden');
        if(hasToolbar){ return }
        
        QuillHelper.initFontSizes()
        QuillHelper.initIcons()
        registerReveFormats()
        ;(Quill.import("ui/icons") as any)["bold"] = renderToStaticMarkup(<AiOutlineBold size={14} />)
        ;(Quill.import("ui/icons") as any)["italic"] = renderToStaticMarkup(<AiOutlineItalic size={14} />)
        ;(Quill.import("ui/icons") as any)["underline"] = renderToStaticMarkup(<AiOutlineUnderline  size={14} />)
        ;(Quill.import("ui/icons") as any)["strike"] = renderToStaticMarkup(<AiOutlineStrikethrough  size={14} />)
        ;(Quill.import("ui/icons") as any)["table-better"] = renderToStaticMarkup(<AiOutlineTable size={16} />)
        
        // Initialize Quill only once
        const quill = new Quill(editorRef.current, {
            theme: "snow",
            readOnly: disableEditMode,
            modules: {
                toolbar: {
                    container: "#toolbar",
                handlers: {
                    // Custom actions, prevent default Quill behavior
                    "more-options": function (this: any) {
                        return false;
                    },
                    "size": function (this: any) {
                        return false;
                    },
                    "header": function (this: any) {
                        return false;
                    },
                    "align-options": function (this: any) {
                        return false;
                    },
                },
            },
                table: false,
                "table-better": EDITOR_TABLE_SETTINGS,
                keyboard: {
                    bindings: { ...QuillTableBetter.keyboardBindings, ...reveKeyboardBindings },
                },
                history: EDITOR_HISTORY_SETTINGS,
            },
        });

        quillRef.current = quill

        const initialRaw = convertRefToAnchor(content ?? '', metadataRef.current?.policies ?? []);
        lastLoadedLetterHtmlRef.current = initialRaw;
        loadQuillHtmlForTableBetter(quillRef.current, initialRaw);
        resetQuillHistoryAfterLoad(quillRef.current);

        // Calculate editor height after initialization and setup observers
        const setupHeightCalculation = () => {
            calculateEditorHeight();
            
            try{
                // Watch for toolbar size changes (e.g., when it wraps)
                if (containerRef.current && window.ResizeObserver) {
                    const toolbar = containerRef.current.querySelector('.ql-toolbar.ql-snow') as HTMLElement;
                    if (toolbar && !resizeObserverRef.current) {
                        resizeObserverRef.current = new ResizeObserver(() => {
                            calculateEditorHeight();
                        });
                        resizeObserverRef.current.observe(toolbar);
                    }
                }
            } catch {
                // No action needed
            }
        };

        // Initial calculation after a short delay to ensure DOM is ready
        setTimeout(() => {
            setupHeightCalculation();
        }, 0);

        // Handle window resize to recalculate height
        const handleResize = () => {
            calculateEditorHeight();
        };
        window.addEventListener('resize', handleResize);

        quillRef.current.on("selection-change", () => {
            updateHistoryToolbarButtons(quill);
            updateFormatLabels(quill);
        });
        
        quillRef.current.on("text-change", () => {
            updateHistoryToolbarButtons(quill);
            updateFormatLabels(quill);

            if(!editorRef || !editorRef?.current){
                return
            }

            const rawHtml = editorRef?.current?.querySelector?.(".ql-editor")?.innerHTML;
            // Strip ql-ui spans (Quill 2 list-marker nodes) — rendering artifacts not part of content
            const strippedHtml = rawHtml?.replace(/<span class="ql-ui"[^>]*><\/span>/g, '') ?? '';
            const html = applyDocumentTableClassToHtml(strippedHtml);
            const convertedHtml = convertRefToAnchor(html, metadataRef.current?.policies ?? []);
            lastLoadedLetterHtmlRef.current = convertedHtml;
            onChangeRef.current?.(convertedHtml);
            // Handle content save to backend
            if(onSaveRef.current){
                handleSaveContent(convertedHtml);
            }
        });

        updateHistoryToolbarButtons(quill);

        // Setup more-options menu
        const moreOptionsHandler = setupMenuListeners(
            '.ql-more-options',
            '.more-options-menu',
            '.more-options-item',
            QuillHelper.handleMoreOptionsItemClick,
            quill,
            updateHistoryToolbarButtons
        );

        // Setup size options menu
        const sizeOptionsHandler = setupMenuListeners(
            'button.ql-size',
            '.size-options-menu',
            '.size-options-item',
            QuillHelper.handleSizeItemClick,
            quill,
            updateHistoryToolbarButtons
        );

        // Setup header options menu
        const headerOptionsHandler = setupMenuListeners(
            'button.ql-header',
            '.header-options-menu',
            '.header-options-item',
            QuillHelper.handleHeaderItemClick,
            quill,
            updateHistoryToolbarButtons
        );

        // Setup align options menu
        const alignOptionsHandler = setupMenuListeners(
            'button.ql-align-options',
            '.align-options-menu',
            '.align-options-item',
            QuillHelper.handleAlignOptionsItemClick,
            quill,
            updateHistoryToolbarButtons
        );

        // Attach scroll prevention to inline formatting buttons
        const formattingBtnCleanups: Array<() => void> = [];
        ['button.ql-bold', 'button.ql-italic', 'button.ql-underline', 'button.ql-strike'].forEach(sel => {
            const btn = document.querySelector(sel) as HTMLElement | null;
            if (btn) formattingBtnCleanups.push(attachScrollPreventionToButton(btn, quill));
        });

        // Reposition the quill-table-better insert picker to fixed so it isn't clipped by the editor layout
        // Reposition the quill-table-better insert picker to fixed so it isn't clipped by the editor layout.
        // Uses MutationObserver on the picker's class list so we don't interfere with quill-table-better's
        // own click/toggle logic — the observer fires as a microtask after ql-hidden is removed, before paint.
        const tableBtn = document.querySelector('button.ql-table-better') as HTMLButtonElement | null;
        let tableBtnScrollTop = 0;
        let tablePickerObserver: MutationObserver | null = null;

        const tableBtnMousedown = () => {
            const editor = editorRef.current?.querySelector('.ql-editor') as HTMLElement | null;
            tableBtnScrollTop = editor?.scrollTop ?? 0;
            // Pre-hide the picker before quill-table-better shows it on the subsequent click,
            // preventing a visible flash at the old position before the MutationObserver repositions it.
            const picker = tableBtn?.querySelector<HTMLElement>('.ql-table-select-container');
            if (picker) picker.style.visibility = 'hidden';
        };
        const tableBtnClick = () => {
            const editor = editorRef.current?.querySelector('.ql-editor') as HTMLElement | null;
            requestAnimationFrame(() => {
                if (editor) editor.scrollTop = tableBtnScrollTop;
                document.querySelectorAll<HTMLElement>('.ql-dropdown-menu').forEach(m => { m.style.display = 'none'; });
            });
        };

        if (tableBtn) {
            tableBtn.addEventListener('mousedown', tableBtnMousedown);
            tableBtn.addEventListener('click', tableBtnClick);

            const picker = tableBtn.querySelector<HTMLElement>('.ql-table-select-container');
            if (picker) {
                tablePickerObserver = new MutationObserver(() => {
                    if (picker.classList.contains('ql-hidden')) return;
                    // Picker just became visible — hide it, reposition with fixed, then reveal
                    const rect = tableBtn.getBoundingClientRect();
                    picker.style.visibility = 'hidden';
                    picker.style.position = 'fixed';
                    picker.style.top = `${rect.bottom + 4}px`;
                    picker.style.left = `${rect.left}px`;
                    requestAnimationFrame(() => {
                        if (picker.classList.contains('ql-hidden')) return;
                        const pr = picker.getBoundingClientRect();
                        if (pr.right > window.innerWidth - 8) {
                            picker.style.left = `${rect.right - pr.width}px`;
                        }
                        picker.style.visibility = '';
                    });
                });
                tablePickerObserver.observe(picker, { attributes: true, attributeFilter: ['class'] });
            }
        }

        // Setup link popup
        const linkPopup = document.getElementById('ql-link-popup') as HTMLElement | null;
        const linkInput = document.getElementById('ql-link-input') as HTMLInputElement | null;
        const linkSaveBtn = document.getElementById('ql-link-save') as HTMLButtonElement | null;

        let linkPopupClickHandler: ((e: MouseEvent) => void) | null = null;

        if (linkPopup && linkInput && linkSaveBtn) {
            const applyLink = () => {
                const url = linkInput.value.trim();
                const rangeIndex = parseInt(linkPopup.dataset.rangeIndex ?? '0', 10);
                const rangeLength = parseInt(linkPopup.dataset.rangeLength ?? '0', 10);
                quill.formatText(rangeIndex, rangeLength, 'link', url || false);
                linkPopup.style.display = 'none';
                updateHistoryToolbarButtons(quill);
            };

            linkSaveBtn.addEventListener('click', applyLink);

            linkInput.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') applyLink();
                if (e.key === 'Escape') { linkPopup.style.display = 'none'; }
            });

            linkPopupClickHandler = (e: MouseEvent) => {
                if (
                    linkPopup.style.display === 'flex' &&
                    !linkPopup.contains(e.target as Node) &&
                    !(document.querySelector('.ql-more-options') as HTMLElement | null)?.contains(e.target as Node)
                ) {
                    linkPopup.style.display = 'none';
                }
            };
            document.addEventListener('mousedown', linkPopupClickHandler, true);
        }

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }

            // Remove resize listener
            window.removeEventListener('resize', handleResize);

            // Remove document click listeners
            if (moreOptionsHandler) document.removeEventListener('mousedown', moreOptionsHandler, true);
            if (sizeOptionsHandler) document.removeEventListener('mousedown', sizeOptionsHandler, true);
            if (headerOptionsHandler) document.removeEventListener('mousedown', headerOptionsHandler, true);
            if (alignOptionsHandler) document.removeEventListener('mousedown', alignOptionsHandler, true);
            if (linkPopupClickHandler) document.removeEventListener('mousedown', linkPopupClickHandler, true);
            if (tableBtn) {
                tableBtn.removeEventListener('mousedown', tableBtnMousedown);
                tableBtn.removeEventListener('click', tableBtnClick);
            }
            formattingBtnCleanups.forEach(cleanup => cleanup());
            if (tablePickerObserver) tablePickerObserver.disconnect();

            // Disconnect ResizeObserver
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            }

            // Save changes on unmount
            const html = quill.root.innerHTML;
            if (html && onSaveRef.current) {
                const convertedHtml = convertRefToAnchor(html, metadataRef.current?.policies ?? []);
                onSaveRef.current(convertedHtml);
            }

            quillRef.current = null;
        };
    }, []);

    // Handle disableEditMode changes
    useEffect(() => {
        if (quillRef.current) {
            quillRef.current.enable(disableEditMode !== true);
        }
    }, [disableEditMode]);

    /**
     * Sync external `content` into Quill when the parent updates (e.g. opening a different letter).
     * `lastLoadedLetterHtmlRef` is updated on every text-change so parent echoes from onChange do not reload.
     */
    useEffect(() => {
        if (!quillRef.current || content === undefined) return;
        const raw = convertRefToAnchor(content, metadataRef.current?.policies ?? []);
        if (raw === lastLoadedLetterHtmlRef.current) return;
        lastLoadedLetterHtmlRef.current = raw;
        const quill = quillRef.current;
        loadQuillHtmlForTableBetter(quill, raw);
        resetQuillHistoryAfterLoad(quill);
    }, [content]);

    // Helper function to setup menu event listeners
    const setupMenuListeners = (
        buttonSelector: string,
        menuSelector: string,
        itemSelector: string,
        onItemClick: (item: HTMLElement, quill: Quill) => void,
        quill: Quill,
        updateHistoryButtons: (quill: Quill) => void
    ) => {
        const button = document.querySelector(buttonSelector) as HTMLButtonElement;
        const menu = document.querySelector(menuSelector) as HTMLDivElement;
        
        if (!button || !menu) return;

        // Quill's own toolbar click listener runs first and calls quill.focus(), which scrolls the
        // editor to the cursor. Save scroll on mousedown and restore it after Quill's handler runs.
        let savedScrollTop = 0;
        button.addEventListener('mousedown', (e) => {
            e.preventDefault();
            const editor = editorRef.current?.querySelector('.ql-editor') as HTMLElement | null;
            savedScrollTop = editor?.scrollTop ?? 0;
        });

        // Toggle menu on button click
        button.addEventListener('click', (e) => {
            e.stopPropagation();
            const editor = editorRef.current?.querySelector('.ql-editor') as HTMLElement | null;
            // Always restore scroll — opening a menu doesn't apply any format
            requestAnimationFrame(() => {
                if (editor) editor.scrollTop = savedScrollTop;
            });

            const isVisible = menu.style.display === 'block';

            // Close all dropdowns before opening a new one (including quill-table-better picker)
            document.querySelectorAll<HTMLElement>('.ql-dropdown-menu').forEach(m => {
                if (m !== menu) m.style.display = 'none';
            });
            // Close quill-table-better insert picker and context menus
            document.querySelector<HTMLElement>('.ql-table-select-container')?.classList.add('ql-hidden');
            document.querySelector<HTMLElement>('.ql-table-menus-container')?.classList.add('ql-hidden');

            if (isVisible) {
                menu.style.display = 'none';
            } else {
                const buttonRect = button.getBoundingClientRect();
                // Use fixed positioning so parent overflow:hidden never clips the menu
                menu.style.position = 'fixed';
                menu.style.top = `${buttonRect.bottom + 4}px`;
                menu.style.left = `${buttonRect.left}px`;
                menu.style.right = 'auto';
                menu.style.display = 'block';

                // Flip to right-aligned if the menu overflows the viewport's right edge
                const menuRect = menu.getBoundingClientRect();
                if (menuRect.right > window.innerWidth - 8) {
                    menu.style.left = 'auto';
                    menu.style.right = `${window.innerWidth - buttonRect.right}px`;
                }
            }
        });

        // Handle menu item clicks
        const items = menu.querySelectorAll(itemSelector);
        items.forEach((item) => {
            item.addEventListener('click', (e) => {
                e.stopPropagation();
                onItemClick(item as HTMLElement, quill);
                menu.style.display = 'none';
                updateHistoryButtons(quill);
            });
        });

        // Close menu when clicking outside
        const outsideClickHandler = (e: MouseEvent) => {
            if (!button.contains(e.target as Node) && !menu.contains(e.target as Node)) {
                menu.style.display = 'none';
            }
        };
        document.addEventListener('mousedown', outsideClickHandler, true);
        return outsideClickHandler;
    };

    const attachScrollPreventionToButton = (button: HTMLElement, quill: Quill): (() => void) => {
        let savedScrollTop = 0;
        let hadSelection = false;

        const onMousedown = () => {
            const editor = editorRef.current?.querySelector('.ql-editor') as HTMLElement | null;
            savedScrollTop = editor?.scrollTop ?? 0;
            const sel = quill.getSelection();
            hadSelection = !!(sel && sel.length > 0);
        };

        const onClick = () => {
            if (hadSelection) return;
            const editor = editorRef.current?.querySelector('.ql-editor') as HTMLElement | null;
            requestAnimationFrame(() => {
                if (editor) editor.scrollTop = savedScrollTop;
            });
        };

        button.addEventListener('mousedown', onMousedown);
        button.addEventListener('click', onClick);

        return () => {
            button.removeEventListener('mousedown', onMousedown);
            button.removeEventListener('click', onClick);
        };
    };

    const handleSaveContent = (content: string) => {
        if(onSaveRef.current){
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            // Guard against an in-progress cut/paste (e.g. Ctrl+X to replace the letter):
            // skip saving a momentarily empty editor so the previous content isn't lost
            // if the user doesn't paste within the debounce window.
            if (ChatHelper.isLetterContentEmpty(content)) {
                return;
            }
            saveTimeoutRef.current = setTimeout(() => {
                onSaveRef.current?.(content || '');
            }, 8000);
        }
    }

    const documentTitle = title ?? (claimId ? `Claim #${claimId} Appeal Letter` : '');

    return (
        <div ref={containerRef} className="h-full flex flex-col">
            {/* Header row: undo/redo · title · DOC · PDF · close */}
            {!hideHeader && <div className="document-editor-header">
                {canGoBack && (
                    <button
                        className="doc-header-btn"
                        title="Back"
                        aria-label="Back to previous view"
                        onClick={onBack}
                    >
                        <BiChevronLeft size={18} />
                    </button>
                )}
                {!disableEditMode && <>
                    <button
                        id="ql-undo"
                        className="doc-header-btn"
                        title="Undo"
                        onClick={() => { quillRef.current?.history.undo(); if (quillRef.current) updateHistoryToolbarButtons(quillRef.current); }}
                    >
                        <BiUndo size={18} />
                    </button>
                    <button
                        id="ql-redo"
                        className="doc-header-btn"
                        title="Redo"
                        onClick={() => { quillRef.current?.history.redo(); if (quillRef.current) updateHistoryToolbarButtons(quillRef.current); }}
                    >
                        <BiRedo size={18} />
                    </button>
                </>}

                <span className="doc-header-title">{documentTitle}</span>

                {
                    canDownloadAttachments && onDownload ?
                    <>
                        <button
                            className="doc-header-btn doc-action-btn"
                            title="Download as Word"
                            disabled={downloadingFormat !== null}
                            onClick={() => { void handleDownload('docx'); }}
                        >
                            DOC
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
                        </button>
                        <button
                            className="doc-header-btn doc-action-btn"
                            title="Download as PDF"
                            disabled={downloadingFormat !== null}
                            onClick={() => { void handleDownload('pdf'); }}
                        >
                            PDF
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
                        </button>
                    </> : null
                }
                
                <button className="doc-header-btn" title="Close editor" onClick={onClose}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                </button>
            </div>}

            {/* Quill formatting toolbar */}
            <div id="toolbar" className={disableEditMode ? 'ql_toolbar-hidden' : undefined}>
                {!disableEditMode && (
                    <span className="ql_toolbar-strip">
                        {/* Font size dropdown */}
                        <span className="ql-formats">
                            <span className="ql_dropdown-anchor">
                                <button className="ql-size ql-toolbar-actions" title='Text size'></button>
                                <div className="size-options-menu ql-dropdown-menu">
                                    {EDITOR_FONT_SIZES.map(fontSize => (
                                        <button
                                            key={fontSize}
                                            className="size-options-item"
                                            style={{ fontSize }}
                                            data-size={fontSize}
                                        >
                                            {fontSize.replace('px', '')}
                                        </button>
                                    ))}
                                </div>
                            </span>
                        </span>

                        {/* Heading dropdown */}
                        <span className="ql-formats">
                            <span className="ql_dropdown-anchor">
                                <button className="ql-header ql-toolbar-actions" title='Heading'></button>
                                <div className="header-options-menu ql-dropdown-menu">
                                    {EDITOR_HEADINGS.map(heading => {
                                        return (
                                            <button
                                                key={heading}
                                                className={`header-options-item ${heading === 'false' ? 'ql_normal' : `ql_heading${heading}`}`}
                                                data-header={heading}
                                            >
                                                <span className="header-item-label">
                                                    {heading === 'false' ? 'Normal' : `Heading ${heading}`}
                                                </span>
                                                <svg className="header-item-check" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                                            </button>
                                        );
                                    })}
                                </div>
                            </span>
                        </span>

                        {/* Basic formatting */}
                        <span className="ql-formats ql-toolbar-actions ql_bius-group">
                            <button className="ql-bold" title='Bold'></button>
                            <button className="ql-italic" title='Italic'></button>
                            <button className="ql-underline" title='Underline'></button>
                            <button className="ql-strike" title='Strikethrough'></button>
                        </span>

                        {/* Text align + list dropdown */}
                        <span className="ql-formats">
                            <span className="ql_dropdown-anchor">
                                <button className="ql-align-options ql-toolbar-actions" title='Text align'></button>
                                <div className="align-options-menu ql-dropdown-menu">
                                    <button className="align-options-item" data-action="align-left">
                                        <svg className="align-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z"/></svg>
                                        <span className="align-item-label">Left</span>
                                        <svg className="align-item-check" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                                    </button>
                                    <button className="align-options-item" data-action="align-center">
                                        <svg className="align-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z"/></svg>
                                        <span className="align-item-label">Center</span>
                                        <svg className="align-item-check" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                                    </button>
                                    <button className="align-options-item" data-action="align-right">
                                        <svg className="align-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z"/></svg>
                                        <span className="align-item-label">Right</span>
                                        <svg className="align-item-check" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                                    </button>
                                    <button className="align-options-item" data-action="align-justify">
                                        <svg className="align-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 21h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18V7H3v2zM3 3v2h18V3H3z"/></svg>
                                        <span className="align-item-label">Justify</span>
                                        <svg className="align-item-check" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                                    </button>
                                    <button className="align-options-item align-options-item-divided" data-action="indent-increase">
                                        <svg className="align-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 21h18v-2H3v2zM3 8v8l4-4-4-4zm8 9h10v-2H11v2zM3 3v2h18V3H3zm8 6h10V7H11v2zm0 4h10v-2H11v2z"/></svg>
                                        <span className="align-item-label">Increase indent</span>
                                    </button>
                                    <button className="align-options-item" data-action="indent-decrease">
                                        <svg className="align-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M11 17h10v-2H11v2zm-8-5l4 4V8l-4 4zm0 9h18v-2H3v2zM3 3v2h18V3H3zm8 6h10V7H11v2zm0 4h10v-2H11v2z"/></svg>
                                        <span className="align-item-label">Decrease indent</span>
                                    </button>
                                    <button className="align-options-item align-options-item-divided" data-action="ordered-list">
                                        <svg className="align-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z"/></svg>
                                        <span className="align-item-label">Numbered list</span>
                                        <svg className="align-item-check" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                                    </button>
                                    <button className="align-options-item" data-action="bullet-list">
                                        <svg className="align-item-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.68-1.5 1.5s.68 1.5 1.5 1.5 1.5-.68 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z"/></svg>
                                        <span className="align-item-label">Bullet list</span>
                                        <svg className="align-item-check" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                                    </button>
                                </div>
                            </span>
                        </span>

                        {/* Table */}
                        <span className="ql-formats">
                            <button className="ql-table-better ql-toolbar-actions" title='Table insert'></button>
                        </span>

                        {/* More Options */}
                        <span className="ql-formats">
                            <span className="ql_dropdown-anchor">
                                <button className="ql-more-options ql-toolbar-actions" title='More options'></button>
                                <div className="more-options-menu ql-dropdown-menu">
                                    <button className="more-options-item" data-action="subscript">
                                        <svg className="more-options-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22 18h-2v1h3v1h-4v-2.5c0-.28.22-.5.5-.5h2v-1h-2.5V15H22c.28 0 .5.22.5.5V16c0 .28-.22.5-.5.5h-2v1H22v.5zM5.88 18h2.66l3.4-5.42h.12l3.4 5.42h2.66l-4.65-7.27L17.81 4h-2.68l-3.07 4.99h-.12L8.85 4H6.19l4.32 6.73L5.88 18z"/></svg>
                                        <span className="more-options-label">Subscript</span>
                                        <svg className="more-options-check" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                                    </button>
                                    <button className="more-options-item" data-action="superscript">
                                        <svg className="more-options-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22 7h-2v1h3v1h-4V6.5c0-.28.22-.5.5-.5h2v-1h-2.5V4H22c.28 0 .5.22.5.5V5c0 .28-.22.5-.5.5h-2V6H22v1zM5.88 20h2.66l3.4-5.42h.12l3.4 5.42h2.66l-4.65-7.27L17.81 6h-2.68l-3.07 4.99h-.12L8.85 6H6.19l4.32 6.73L5.88 20z"/></svg>
                                        <span className="more-options-label">Superscript</span>
                                        <svg className="more-options-check" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                                    </button>
                                    <button className="more-options-item align-options-item-divided" data-action="blockquote">
                                        <svg className="more-options-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z"/></svg>
                                        <span className="more-options-label">Blockquote</span>
                                        <svg className="more-options-check" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                                    </button>
                                    <button className="more-options-item align-options-item-divided" data-action="link">
                                        <svg className="more-options-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>
                                        <span className="more-options-label">Insert Link</span>
                                        <svg className="more-options-check" width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                                    </button>
                                    <button className="more-options-item align-options-item-divided" data-action="clean">
                                        <svg className="more-options-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3.27 5L2 6.27l6.97 6.97L6.5 19h3l1.57-3.66L16.73 21 18 19.73 3.55 5.27 3.27 5zM6 5v.18L8.82 8h2.4l-.72 1.68 2.1 2.1L14.21 8H20V5H6z"/></svg>
                                        <span className="more-options-label">Clear Formatting</span>
                                    </button>
                                </div>
                            </span>
                        </span>
                    </span>
                )}
            </div>

            {/* Link insert popup — shown with position:fixed via JS when link action is triggered */}
            <div id="ql-link-popup" className="ql_link-popup">
                <span className="ql_link-label">Enter link:</span>
                <input
                    id="ql-link-input"
                    type="text"
                    placeholder="https://"
                    className="ql_link-input"
                />
                <button id="ql-link-save" className="ql_link-save-btn">
                    Save
                </button>
            </div>

            <div ref={editorRef} className="flex-1 min-h-0" />
        </div>
    )
}

export default DocumentEditor